const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export function isAdmin(user) {
  return user?.role?.toLowerCase() === 'administrator';
}

export function isSupervisor(user) {
  return user?.role?.toLowerCase() === 'supervisor';
}

export function isSupervisorOrAdmin(user) {
  return isSupervisor(user) || isAdmin(user);
}

export async function login(username, password) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || 'Login failed');
    err.status = res.status;
    throw err;
  }
  return data;
}

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (res.status === 401 || res.status === 422) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    sessionStorage.setItem('auth_redirect_reason', 'session_expired');
    window.location.href = '/login';
    return;
  }
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

export async function listGolfCourses({ isArchived = false, cursor, limit = 20 } = {}) {
  const params = new URLSearchParams();
  params.set('is_archived', String(isArchived));
  if (cursor) params.set('cursor', cursor);
  params.set('limit', String(limit));
  return apiFetch(`/golf-courses?${params.toString()}`);
}

export async function getGolfCourse(id) {
  return apiFetch(`/golf-courses/${id}`);
}

export async function createGolfCourse(data) {
  return apiFetch('/golf-courses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateGolfCourse(id, data) {
  return apiFetch(`/golf-courses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function archiveGolfCourse(id) {
  return apiFetch(`/golf-courses/${id}`, {
    method: 'DELETE',
  });
}

export async function exportGolfCoursesCSV({ isArchived = false, name, holes } = {}) {
  const params = new URLSearchParams();
  params.set('is_archived', String(isArchived));
  if (name && name !== '') params.set('name', name);
  if (holes && holes !== '') params.set('holes', holes);

  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE}/golf-courses/export/csv?${params.toString()}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (res.status === 401 || res.status === 422) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    sessionStorage.setItem('auth_redirect_reason', 'session_expired');
    window.location.href = '/login';
    return;
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Export failed');
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'golf_courses.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Calendar — Golf Courses (all active, for dropdown)
// ---------------------------------------------------------------------------

export async function listAllGolfCourses() {
  const params = new URLSearchParams();
  params.set('is_archived', 'false');
  params.set('limit', '100');
  return apiFetch(`/golf-courses?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Calendar — Tee Times
// ---------------------------------------------------------------------------

export async function listTeeTimes({ date, golfCourseId, hole } = {}) {
  const params = new URLSearchParams();
  params.set('is_archived', 'false');
  params.set('limit', '200');
  if (date) params.set('date', date);
  if (golfCourseId != null) params.set('golf_course_id', String(golfCourseId));
  if (hole != null) params.set('hole', String(hole));
  return apiFetch(`/tee-times?${params.toString()}`);
}

export async function updateTeeTime(id, data) {
  return apiFetch(`/tee-times/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function createTeeTime(data) {
  return apiFetch('/tee-times', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export async function getSetting(key) {
  return apiFetch(`/settings/${key}`);
}

export async function listSettings() {
  return apiFetch('/settings');
}

export async function updateSetting(key, value) {
  return apiFetch(`/settings/${key}`, { method: 'PUT', body: JSON.stringify({ value }) });
}

// ---------------------------------------------------------------------------
// Calendar — Bookings
// ---------------------------------------------------------------------------

export async function listBookings({
  date, isArchived = false, limit = 200, cursor,
  bookingNumber, time, bookedBy, golfCourseId, hole,
  isProblematic, isValidated, isCancelled,
} = {}) {
  const params = new URLSearchParams();
  if (date)          params.set('date', date);
  params.set('is_archived', String(isArchived));
  params.set('limit', String(limit));
  if (cursor)        params.set('cursor', cursor);
  if (bookingNumber) params.set('booking_number', bookingNumber);
  if (time)          params.set('time', time);
  if (bookedBy)      params.set('booked_by', bookedBy);
  if (golfCourseId)  params.set('golf_course_id', String(golfCourseId));
  if (hole)          params.set('hole', String(hole));
  if (isProblematic != null) params.set('is_problematic', String(isProblematic));
  if (isValidated    != null) params.set('is_validated',    String(isValidated));
  if (isCancelled   != null) params.set('is_cancelled',    String(isCancelled));
  return apiFetch(`/bookings?${params.toString()}`);
}

export async function getBooking(id) {
  return apiFetch(`/bookings/${id}`);
}

export async function createBooking(data) {
  return apiFetch('/bookings', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBooking(id, data) {
  return apiFetch(`/bookings/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function archiveBooking(id) {
  return apiFetch(`/bookings/${id}`, {
    method: 'DELETE',
  });
}

export async function exportBookingsCSV({
  isArchived = false,
  date,
  bookingNumber,
  time,
  bookedBy,
  golfCourseId,
  hole,
  isProblematic,
  isValidated,
  isCancelled,
} = {}) {
  const params = new URLSearchParams();
  params.set('is_archived', String(isArchived));
  if (date)                           params.set('date', date);
  if (bookingNumber)                  params.set('booking_number', bookingNumber);
  if (time)                           params.set('time', time);
  if (bookedBy)                       params.set('booked_by', bookedBy);
  if (golfCourseId)                   params.set('golf_course_id', String(golfCourseId));
  if (hole)                           params.set('hole', String(hole));
  if (isProblematic != null)          params.set('is_problematic', String(isProblematic));
  if (isValidated   != null)          params.set('is_validated',   String(isValidated));
  if (isCancelled   != null)          params.set('is_cancelled',   String(isCancelled));

  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE}/bookings/export/csv?${params.toString()}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (res.status === 401 || res.status === 422) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    sessionStorage.setItem('auth_redirect_reason', 'session_expired');
    window.location.href = '/login';
    return;
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Export failed');
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bookings.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function recheckBooking(id) {
  return apiFetch(`/bookings/${id}/recheck`, { method: 'POST' });
}

export async function approveBooking(id) {
  return apiFetch(`/bookings/${id}/approve`, { method: 'POST' });
}

export async function cancelBooking(id) {
  return apiFetch(`/bookings/${id}/cancel`, { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Members — search (for booking autocomplete and member picker)
// ---------------------------------------------------------------------------

export async function searchMembers({ name, memberNumber, cursor, limit = 20, cacheOnly = false } = {}) {
  const params = new URLSearchParams();
  if (name) params.set('name', name);
  if (memberNumber) params.set('member_number', memberNumber);
  if (cursor) params.set('cursor', cursor);
  params.set('limit', String(limit));
  if (cacheOnly) params.set('cache_only', 'true');
  return apiFetch(`/members/search?${params.toString()}`);
}

export async function getMember(memberNumber) {
  return apiFetch(`/members/${encodeURIComponent(memberNumber)}`);
}

// ---------------------------------------------------------------------------
// Calendar — Blocks
// ---------------------------------------------------------------------------

export async function listBlocks({
  date, name, golfCourseId, isArchived = false, limit = 200,
  dateFrom, dateTo, cursor,
} = {}) {
  const params = new URLSearchParams();
  params.set('is_archived', String(isArchived));
  params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  if (name) params.set('name', name);
  // date shorthand sets both date_from and date_to to the same value
  if (date) {
    params.set('date_from', date);
    params.set('date_to', date);
  } else {
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
  }
  if (golfCourseId != null) params.set('golf_course_id', String(golfCourseId));
  return apiFetch(`/blocks?${params.toString()}`);
}

export async function getBlock(id) {
  return apiFetch(`/blocks/${id}`);
}

// Raw block fetch that preserves affected_bookings from 400 responses.
// Throws an enhanced Error with an affectedBookings property when the backend
// returns a conflict.
async function blockMutate(path, method, data) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data),
  });
  if (res.status === 401 || res.status === 422) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    sessionStorage.setItem('auth_redirect_reason', 'session_expired');
    window.location.href = '/login';
    return;
  }
  const body = await res.json();
  if (!res.ok) {
    const err = new Error(body.error || 'Request failed');
    err.affectedBookings = body.affected_bookings || null;
    throw err;
  }
  return body;
}

export async function createBlock(data) {
  return blockMutate('/blocks', 'POST', data);
}

export async function updateBlock(id, data) {
  return blockMutate(`/blocks/${id}`, 'PUT', data);
}

export async function archiveBlock(id) {
  return apiFetch(`/blocks/${id}`, { method: 'DELETE' });
}

export async function exportBlocksCSV({ isArchived = false, name, golfCourseId, dateFrom, dateTo } = {}) {
  const params = new URLSearchParams();
  params.set('is_archived', String(isArchived));
  if (name) params.set('name', name);
  if (golfCourseId) params.set('golf_course_id', String(golfCourseId));
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);

  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE}/blocks/export/csv?${params.toString()}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (res.status === 401 || res.status === 422) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    sessionStorage.setItem('auth_redirect_reason', 'session_expired');
    window.location.href = '/login';
    return;
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Export failed');
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'blocks.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Calendar — Block Types
// ---------------------------------------------------------------------------

export async function listBlockTypes({ isArchived = false, limit = 100 } = {}) {
  const params = new URLSearchParams();
  params.set('is_archived', String(isArchived));
  params.set('limit', String(limit));
  return apiFetch(`/block-types?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export async function getDashboardKPIs(date) {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  return apiFetch(`/dashboard/kpis?${params.toString()}`);
}

export async function getCheckinStatus(date) {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  return apiFetch(`/dashboard/checkin-status?${params.toString()}`);
}

export async function getPlayers7Days(fromDate) {
  const params = new URLSearchParams();
  if (fromDate) params.set('from_date', fromDate);
  return apiFetch(`/dashboard/players-7days?${params.toString()}`);
}

export async function getUpcomingBlocks() {
  return apiFetch('/dashboard/upcoming-blocks');
}

export async function getUnvalidatedBookings() {
  return apiFetch('/dashboard/unvalidated-bookings');
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export async function listUsers({ isArchived = false, cursor, limit = 20 } = {}) {
  const params = new URLSearchParams();
  params.set('is_archived', String(isArchived));
  if (cursor) params.set('cursor', cursor);
  params.set('limit', String(limit));
  return apiFetch(`/users?${params.toString()}`);
}

export async function getUser(id) {
  return apiFetch(`/users/${id}`);
}

export async function createUser(data) {
  return apiFetch('/users', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateUser(id, data) {
  return apiFetch(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function archiveUser(id) {
  return apiFetch(`/users/${id}`, { method: 'DELETE' });
}

export async function changeUserPassword(id, data) {
  return apiFetch(`/users/${id}/password`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function exportUsersCSV({ isArchived = false, role } = {}) {
  const params = new URLSearchParams();
  params.set('is_archived', String(isArchived));
  if (role && role !== '') params.set('role', role);

  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE}/users/export/csv?${params.toString()}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (res.status === 401 || res.status === 422) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    sessionStorage.setItem('auth_redirect_reason', 'session_expired');
    window.location.href = '/login';
    return;
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Export failed');
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'users.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Scheduler
// ---------------------------------------------------------------------------

export async function runScheduler(targetDate) {
  return apiFetch('/scheduler/run', {
    method: 'POST',
    body: JSON.stringify({ target_date: targetDate }),
  });
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

export async function getEntityHistory(path, { cursor, limit = 20 } = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  return apiFetch(`${path}/history?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export async function getBookingsReport({ dateFrom, dateTo, courseId, hole } = {}) {
  const params = new URLSearchParams();
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo)   params.set('date_to', dateTo);
  if (courseId) params.set('course_id', String(courseId));
  if (hole)     params.set('hole', String(hole));
  return apiFetch(`/reports/bookings?${params.toString()}`);
}

export async function getOccupancyReport({ dateFrom, dateTo, courseId } = {}) {
  const params = new URLSearchParams();
  if (dateFrom)  params.set('date_from', dateFrom);
  if (dateTo)    params.set('date_to', dateTo);
  if (courseId)  params.set('course_id', String(courseId));
  return apiFetch(`/reports/occupancy?${params.toString()}`);
}

export async function getPlayersReport({
  dateFrom, dateTo, courseId, hole, name, status, playerType,
  bookingNumber, sortBy, sortDir,
} = {}) {
  const params = new URLSearchParams();
  if (dateFrom)      params.set('date_from', dateFrom);
  if (dateTo)        params.set('date_to', dateTo);
  if (courseId)      params.set('course_id', String(courseId));
  if (hole)          params.set('hole', String(hole));
  if (name)          params.set('name', name);
  if (status)        params.set('status', status);
  if (playerType)    params.set('type', playerType);
  if (bookingNumber) params.set('booking_number', bookingNumber);
  if (sortBy)        params.set('sort_by', sortBy);
  if (sortDir)       params.set('sort_dir', sortDir);
  return apiFetch(`/reports/players?${params.toString()}`);
}

export async function getTopMembersReport({
  dateFrom, dateTo, courseId, cursor, limit = 20, bookingsLimit = 20,
} = {}) {
  const params = new URLSearchParams();
  if (dateFrom)  params.set('date_from', dateFrom);
  if (dateTo)    params.set('date_to', dateTo);
  if (courseId)  params.set('course_id', String(courseId));
  if (cursor)    params.set('cursor', cursor);
  params.set('limit', String(limit));
  params.set('bookings_limit', String(bookingsLimit));
  return apiFetch(`/reports/top-members?${params.toString()}`);
}

export async function getTopMemberBookings(memberId, {
  dateFrom, dateTo, courseId, cursor, limit = 20,
} = {}) {
  const params = new URLSearchParams();
  if (dateFrom)  params.set('date_from', dateFrom);
  if (dateTo)    params.set('date_to', dateTo);
  if (courseId)  params.set('course_id', String(courseId));
  if (cursor)    params.set('cursor', cursor);
  params.set('limit', String(limit));
  return apiFetch(`/reports/top-members/${memberId}/bookings?${params.toString()}`);
}

export async function getHeatmapReport({ dateFrom, dateTo, courseId } = {}) {
  const params = new URLSearchParams();
  if (dateFrom)  params.set('date_from', dateFrom);
  if (dateTo)    params.set('date_to', dateTo);
  if (courseId)  params.set('course_id', String(courseId));
  return apiFetch(`/reports/heatmap?${params.toString()}`);
}
